/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { Fragment, useCallback } from "react";
import styled from "styled-components";
import Text from "../../ui/Text";
import FormatPrice from "../../formatters/FormatPrice";
import LineItem from "../LineItem";
import FormatDuration from "../../formatters/FormatDuration";
import Icon from "../../ui/Icon";
import TreeChild from "../tree/TreeChild";
import TreeItemChilds from "../tree/TreeItemChilds";
import { getServiceSessionCount, mergeProductAndAddonLines } from "../../../utils/service";
import { CART_LINE_ID_FIELD } from "../../../constants/constants";

const SummaryTable = styled.div`
  display: grid;
  grid-template-columns: ${({ $hasDiscount }) =>
    $hasDiscount ? "1fr auto auto auto auto" : "1fr auto auto auto"};
  width: 100%;
  text-align: left;
  overflow-x: auto;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-template-columns: ${({ $hasDiscount }) =>
    `minmax(300px, 1fr) repeat(${$hasDiscount ? 4 : 3}, minmax(max-content, auto))`};
  }
`;

const SummaryTableHeader = styled.div`
  display: contents;
`;

const SummaryTableHeaderItem = styled.div`
  padding: 2px 8px;
  background: rgba(${({ theme }) => theme.colors.border.rgba});

  &:first-child {
    padding-left: 16px;
  }

  &:last-child {
    padding-right: 16px;
  }
`;

const SummaryTableRow = styled.div`
  display: contents;
`;

const BorderBottom = styled.div`
display: contents;
  position: relative;

  &::after {
    content: "";
    display: block;
    border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
    grid-column: 1 / -1;
    width: 100%;
  }
`;

const SummaryTableCell = styled.div`
  padding: 8px;

  &:first-child {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:last-child {
    padding-right: 16px;
  }
`;

const SummaryTableSubCell = styled.div`
  padding: 4px 9px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GroupBookingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SummaryItemsTable = ({ items, invoice, renderLineExtra }) => {
  const getInvoiceLineForItem = useCallback(
    (item, bookingIds = null) => {
      if (bookingIds) {
        const ids = Array.isArray(bookingIds) ? bookingIds : [bookingIds];
        const matchingLines = invoice.lines.filter(
          (line) => Number(line.product_id) === Number(item.id) && ids.includes(line.booking_id)
        );

        if (!matchingLines.length) {
          return { totalPrice: 0, tax: 0, discount: 0, qty: 0, bookingIds: [] };
        }

        return matchingLines.reduce(
          (acc, line) => ({
            price: line.deposit_price && invoice.rest_amount > 0 ? line.deposit_price : line.price,
            totalPrice: acc.totalPrice + (line.amount || 0),
            tax: acc.tax + (line.tax_amount || 0),
            discount: acc.discount + (line.discount || 0),
            qty: acc.qty + 1,
            bookingIds: line.booking_ids || [],
          }),
          { totalPrice: 0, tax: 0, discount: 0, qty: 0, bookingIds: [] }
        );
      }

      const invoiceLine = invoice.lines.find((line) => {
        // Invoice-line items carry the line id — match on it directly (any line type).
        if (item.lineId != null) {
          return String(line.id) === String(item.lineId);
        }
        if (line.bookings?.length) {
          return line.bookings[0].service_id === item.id;
        }
        const idField = CART_LINE_ID_FIELD[item.type];
        if (idField) {
          return Number(line[idField]) === Number(item.id);
        }
        return false;
      });

      if (!invoiceLine) {
        return { totalPrice: 0, tax: 0, discount: 0, bookingIds: [] };
      }

      return {
        price: invoiceLine.deposit_price && invoice.rest_amount > 0 ? invoiceLine.deposit_price : invoiceLine.price,
        totalPrice: invoiceLine.amount || 0,
        tax: invoiceLine.tax_amount || 0,
        discount: invoiceLine.discount || 0,
        bookingIds: invoiceLine.booking_ids || [],
      };
    },
    [invoice]
  );

  const hasDiscount = invoice.discount_amount > 0;

  return (
    <SummaryTable $hasDiscount={hasDiscount}>
      <SummaryTableHeader>
        <SummaryTableHeaderItem>
          <Text size="small" color="dark" weight="bold">
            Cart items
          </Text>
        </SummaryTableHeaderItem>
        <SummaryTableHeaderItem>
          <Text size="small" color="dark" weight="bold">
            Price
          </Text>
        </SummaryTableHeaderItem>
        {hasDiscount && (
          <SummaryTableHeaderItem>
            <Text size="small" color="dark" weight="bold">
              Discount
            </Text>
          </SummaryTableHeaderItem>
        )}
        <SummaryTableHeaderItem>
          <Text size="small" color="dark" weight="bold">
            Tax
          </Text>
        </SummaryTableHeaderItem>
        <SummaryTableHeaderItem>
          <Text size="small" color="dark" weight="bold">
            Amount
          </Text>
        </SummaryTableHeaderItem>
      </SummaryTableHeader>
      {items.map((item) => {
        const { totalPrice, price, tax, discount, bookingIds } = getInvoiceLineForItem(item);
        const sessionCount = getServiceSessionCount(item);
        const quantity = item.qty ?? (CART_LINE_ID_FIELD[item.type] ? 1 : sessionCount);
        const groupBookingCount = item.group_booking_count || 0;
        const itemProducts = mergeProductAndAddonLines(item.addons, item.products, groupBookingCount);
        const lineExtra = renderLineExtra?.(item);

        const itemKey = `${item.type ?? "service"}-${item.lineId ?? item.id}`;
        return (
          <Fragment key={itemKey}>
            <SummaryTableRow>
              <SummaryTableCell>
                <LineItem
                  showTooltip
                  quantity={quantity}
                  name={item.name}
                  metadata={[
                    item.duration > 0 && (
                      <Text key="duration">
                        <FormatDuration value={item.duration} type={FormatDuration.TYPE.HOUR} />
                      </Text>
                    ),
                    !!groupBookingCount && (
                      <GroupBookingInfo key="group">
                        <Text>{groupBookingCount}</Text>
                        <Icon.Users color="secondary" />
                      </GroupBookingInfo>
                    ),
                  ]}
                />
                {lineExtra}
              </SummaryTableCell>
              <SummaryTableCell>
                <Text color="dark">
                  <FormatPrice value={price} currency={invoice.currency} />
                </Text>
              </SummaryTableCell>
              {hasDiscount && (
                <SummaryTableCell>
                  <Text color="dark">
                    <FormatPrice value={discount} currency={invoice.currency} />
                  </Text>
                </SummaryTableCell>
              )}
              <SummaryTableCell>
                <Text color="dark">
                  <FormatPrice value={tax} currency={invoice.currency} />
                </Text>
              </SummaryTableCell>
              <SummaryTableCell>
                <Text color="dark">
                  <FormatPrice value={totalPrice} currency={invoice.currency} />
                </Text>
              </SummaryTableCell>
              {itemProducts.length === 0 && <BorderBottom />}
            </SummaryTableRow>

            {itemProducts.map((product, idx) => {
              const { totalPrice, price, tax, discount, qty: matchedQty } = getInvoiceLineForItem(product, bookingIds);
              const quantity = product.isAttribute && matchedQty > 0 ? matchedQty : product.qty;

              return <SummaryTableRow key={product.id}>
                <SummaryTableSubCell>
                  <TreeItemChilds>
                    <TreeChild key={product.id} $isLast={idx === itemProducts.length - 1}>
                      <LineItem
                        quantity={quantity}
                        name={product.name}
                        showTooltip
                        metadata={[
                          product.duration > 0 && (
                            <Text key="duration">
                              <FormatDuration value={product.duration} type={FormatDuration.TYPE.HOUR} />
                            </Text>
                          ),
                        ]}
                      />
                    </TreeChild>
                  </TreeItemChilds>
                </SummaryTableSubCell>
                <SummaryTableSubCell>
                  <Text color="dark">
                    <FormatPrice value={price} currency={invoice.currency} />
                  </Text>
                </SummaryTableSubCell>
                {hasDiscount && (
                  <SummaryTableSubCell>
                    <Text color="dark">
                      <FormatPrice value={discount} currency={invoice.currency} />
                    </Text>
                  </SummaryTableSubCell>
                )}
                <SummaryTableSubCell>
                  <Text color="dark">
                    <FormatPrice value={tax} currency={invoice.currency} />
                  </Text>
                </SummaryTableSubCell>
                <SummaryTableSubCell>
                  <Text color="dark">
                    <FormatPrice value={totalPrice} currency={invoice.currency} />
                  </Text>
                </SummaryTableSubCell>
                {idx === itemProducts.length - 1 && <BorderBottom />}
              </SummaryTableRow>
            })}
          </Fragment>
        );
      })}
    </SummaryTable>
  );
};

export default SummaryItemsTable;
